import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Save, RotateCcw, ExternalLink, LayoutTemplate, Eye } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea } from "./_ui";

const FIELDS = [
  { group: "Hero — Section principale", items: [
    { key: "hero_title", label: "Titre géant", type: "text" },
    { key: "hero_subtitle", label: "Sous-titre or (overline)", type: "text" },
    { key: "hero_tagline", label: "Tagline (texte blanc)", type: "text" },
    { key: "hero_devise", label: "Devise italique", type: "text" },
  ]},
  { group: "Hero — Boutons d'appel à l'action", items: [
    { key: "hero_cta_primary_label", label: "CTA principal — texte", type: "text" },
    { key: "hero_cta_primary_link", label: "CTA principal — lien", type: "text", placeholder: "/organisation" },
    { key: "hero_cta_secondary_label", label: "CTA secondaire — texte", type: "text" },
    { key: "hero_cta_secondary_link", label: "CTA secondaire — lien", type: "text", placeholder: "/contact" },
  ]},
  { group: "Bandeau Identité", items: [
    { key: "diaspora_band_text", label: "Texte du bandeau (séparé par ·)", type: "text" },
  ]},
  { group: "Section Vision", items: [
    { key: "vision_overline", label: "Overline (or)", type: "text" },
    { key: "vision_title", label: "Titre", type: "text" },
    { key: "vision_paragraph", label: "Paragraphe", type: "textarea" },
    { key: "pillar_1_label", label: "Pilier 1", type: "text" },
    { key: "pillar_2_label", label: "Pilier 2", type: "text" },
    { key: "pillar_3_label", label: "Pilier 3", type: "text" },
  ]},
];

export default function CMSVitrinePanel() {
  const [data, setData] = useState(null);
  const [original, setOriginal] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cms/homepage").then((r) => { setData(r.data); setOriginal(r.data); });
  }, []);

  const update = (key, value) => setData({ ...data, [key]: value });

  const dirty = data && original && Object.keys(data).some((k) => data[k] !== original[k]);

  const save = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      const r = await api.put("/admin/cms/homepage", data);
      setData(r.data); setOriginal(r.data);
      toast.success("Page d'accueil mise à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const reset = () => setData(original);

  if (!data) return (<><PageHeader title="CMS Vitrine" /><Spinner /></>);

  return (
    <div data-testid="admin-cms-vitrine">
      <PageHeader
        title="CMS Vitrine"
        subtitle="Édition en direct du contenu de la page d'accueil A.F.B. — sans intervention technique."
        action={
          <div className="flex gap-2">
            <SecondaryButton as={Link}>
              <Link to="/" target="_blank" className="inline-flex items-center gap-2">
                <Eye size={14} /> Aperçu
              </Link>
            </SecondaryButton>
            {dirty && <SecondaryButton onClick={reset} data-testid="cms-reset"><RotateCcw size={14} /> Annuler</SecondaryButton>}
            <PrimaryButton onClick={save} disabled={busy || !dirty} data-testid="cms-save">
              <Save size={14} /> {busy ? "Enregistrement…" : "Publier les modifications"}
            </PrimaryButton>
          </div>
        }
      />

      {dirty && (
        <div className="mb-5 px-4 py-3 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-sm text-[#D4AF37] flex items-center gap-2">
          <LayoutTemplate size={14} />
          Modifications non publiées — cliquez sur « Publier » pour les rendre visibles sur le site.
        </div>
      )}

      <div className="space-y-6">
        {FIELDS.map((group) => (
          <Card key={group.group} className="p-6" data-testid={`cms-group-${group.group.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
            <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-4">{group.group}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {group.items.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <label className="text-xs text-slate-400 mb-1.5 block">{f.label}</label>
                  {f.type === "textarea" ? (
                    <Textarea
                      value={data[f.key] || ""}
                      onChange={(e) => update(f.key, e.target.value)}
                      rows={4}
                      data-testid={`cms-${f.key}`}
                    />
                  ) : (
                    <Input
                      value={data[f.key] || ""}
                      placeholder={f.placeholder}
                      onChange={(e) => update(f.key, e.target.value)}
                      data-testid={`cms-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6 text-xs text-slate-500 inline-flex items-center gap-1.5">
        <ExternalLink size={11} /> Les modifications sont appliquées immédiatement sur la page d'accueil après publication.
      </div>
    </div>
  );
}
